/**
 * 產品模組 - 輔助函式
 */

import {
  Product,
  ProductStatus,
  ItemStats,
  PRODUCT_STATUS_LABELS,
  PRODUCT_STATUS_COLORS,
  DISPLAY_TAGS,
  DisplayTagId,
} from './types';

// 從 equipment-item 模組引入品項相關型別
import type { EquipmentItem, EquipmentItemStatus } from '../equipment-item/types';
import { 
  getStatusConfig,
  getStatusName,
  calculateItemStats as calculateEquipmentItemStats 
} from '../equipment-item/helpers';

// ============================================
// 產品狀態相關
// ============================================

/**
 * 取得產品狀態資訊
 */
export function getProductStatusInfo(status: ProductStatus) {
  return {
    name: PRODUCT_STATUS_LABELS[status] || '未知',
    ...PRODUCT_STATUS_COLORS[status] || PRODUCT_STATUS_COLORS.inactive,
  };
}

/**
 * 取得所有產品狀態選項（用於下拉選單）
 */
export function getProductStatusOptions() {
  return Object.entries(PRODUCT_STATUS_LABELS).map(([value, label]) => ({
    value: value as ProductStatus,
    label,
  }));
}

// ============================================
// 品項狀態相關（使用 equipment-item 模組）
// ============================================

/**
 * 取得品項狀態資訊
 */
export function getItemStatusInfo(status: EquipmentItemStatus) {
  const config = getStatusConfig(status);
  return {
    name: config.name,
    bgLight: config.bgLight,
    text: config.textColor,
  };
}

/**
 * 取得所有品項狀態選項（用於下拉選單）
 */
export function getItemStatusOptions() {
  return [
    { value: 'available' as EquipmentItemStatus, label: '在庫' },
    { value: 'rented' as EquipmentItemStatus, label: '出機' },
    { value: 'maintenance' as EquipmentItemStatus, label: '維修' },
    { value: 'retired' as EquipmentItemStatus, label: '報廢/處分' },
  ];
}

// ============================================
// 顯示標籤相關
// ============================================

/**
 * 相容舊中文標籤與 canonical machine key 的對照表
 * 用於將舊資料中的中文標籤或舊 key 轉換為 canonical key
 */
export const LEGACY_TAG_TO_CANONICAL: Record<string, string> = {
  '熱門': 'hot',
  '新上架': 'new',
  '新品': 'new',
  '預計進貨': 'incoming',
  '即將上架': 'incoming',
  '需搭人員': 'staff_required',
  '需搭工作人員': 'staff_required',
  '需搦人員': 'staff_required',
  '出班組限定': 'crew_only',
  '精選': 'featured',
  '精选': 'featured',
  'おすすめ': 'featured',
  'coming': 'incoming',
  'coming_soon': 'incoming',
  'crew_package': 'crew_only',
  'crew_required': 'staff_required',
  'staff_pick': 'hot',
};

/**
 * 將 display_tags 陣列正規化：legacy 轉換 → 過濾非法值 → 去重複
 * 回傳的標籤皆為 canonical machine key
 */
export function normalizeDisplayTags(tags: string[]): string[] {
  const validValues = new Set(DISPLAY_TAGS.map((d) => d.value));
  return [
    ...new Set(
      tags
        .map((t) => LEGACY_TAG_TO_CANONICAL[t] ?? t)
        .filter((t) => validValues.has(t))
    ),
  ];
}

/**
 * 取得產品的最終 display_tags（只正規化 display_tags 本身）
 * 不會因 is_featured 自動補上 'featured'
 * canonical source of truth：display_tags
 */
export function getProductDisplayTags(
  product: Pick<Product, 'display_tags'>
): string[] {
  return normalizeDisplayTags(product.display_tags || []);
}

/**
 * 檢查商品是否存在舊版 is_featured 旗標，但 display_tags 未包含 'featured' 的情況
 * 用於後台提示管理員同步
 */
export function hasLegacyFeaturedMismatch(
  product: Pick<Product, 'display_tags' | 'is_featured'>
): boolean {
  return product.is_featured === true && !normalizeDisplayTags(product.display_tags || []).includes('featured');
}

/**
 * 取得顯示標籤資訊
 */
export function getDisplayTagInfo(tagId: DisplayTagId) {
  return DISPLAY_TAGS.find(tag => tag.id === tagId);
}

/**
 * 取得所有顯示標籤選項
 */
export function getDisplayTagOptions() {
  return DISPLAY_TAGS.map(tag => ({
    value: tag.id,
    label: tag.name_zh,
  }));
}

// ============================================
// 品項統計相關
// ============================================

/**
 * 計算品項統計
 */
export function calculateItemStats(items: EquipmentItem[]): ItemStats {
  const stats = calculateEquipmentItemStats(items);
  return {
    total: stats.total,
    available: stats.available,
    rented: stats.rented,
    maintenance: stats.maintenance,
  };
}

/**
 * 依產品 ID 分組計算品項統計
 */
export function calculateItemStatsByProduct(items: EquipmentItem[]): Record<string, ItemStats> {
  const statsMap: Record<string, ItemStats> = {};

  items.forEach(item => {
    if (!statsMap[item.product_id]) {
      statsMap[item.product_id] = { total: 0, available: 0, rented: 0, maintenance: 0 };
    }
    
    const stats = statsMap[item.product_id];
    stats.total++;
    
    switch (item.status) {
      case 'available':
        stats.available++;
        break;
      case 'rented':
        stats.rented++;
        break;
      case 'maintenance':
        stats.maintenance = (stats.maintenance || 0) + 1;
        break;
    }
  });

  return statsMap;
}

// ============================================
// 產品格式化相關
// ============================================

/**
 * 格式化產品名稱（含品牌）
 */
export function formatProductName(product: Pick<Product, 'name_zh' | 'brand'>, includeBrand = true): string {
  if (includeBrand && product.brand) {
    return `${product.brand} ${product.name_zh}`;
  }
  return product.name_zh;
}

/**
 * 格式化價格顯示
 */
export function formatPrice(price: number, currency = 'NT$'): string {
  return `${currency} ${price.toLocaleString()}`;
}

/**
 * 格式化每日租金顯示
 */
export function formatDailyPrice(price: number, currency = 'NT$'): string {
  return `${formatPrice(price, currency)}/日`;
}

// ============================================
// 品項格式化相關
// ============================================

/**
 * 格式化品項序號顯示
 */
export function formatSerialNumber(serialNumber: string): string {
  return serialNumber.toUpperCase();
}

/**
 * 產生品項序號建議
 */
export function generateSerialNumberSuggestion(
  productCode: string | undefined,
  existingSerials: string[],
  prefix = ''
): string {
  const baseCode = productCode || 'ITEM';
  const fullPrefix = prefix ? `${prefix}-${baseCode}` : baseCode;
  
  // 找出現有序號中最大的數字
  const numbers = existingSerials
    .filter(sn => sn.startsWith(fullPrefix))
    .map(sn => {
      const match = sn.match(/(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    });
  
  const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
  const nextNumber = (maxNumber + 1).toString().padStart(3, '0');
  
  return `${fullPrefix}-${nextNumber}`;
}

// ============================================
// 關聯標籤相關
// ============================================

/**
 * 解析關聯標籤字串
 */
export function parseRelatedTags(tagsString: string | null | undefined): string[] {
  if (!tagsString) return [];
  return tagsString
    .split(/\s+/)
    .filter(tag => tag.startsWith('#'))
    .map(tag => tag.slice(1).trim())
    .filter(tag => tag.length > 0);
}

/**
 * 格式化關聯標籤為字串
 */
export function formatRelatedTags(tags: string[]): string {
  return tags.map(tag => `#${tag}`).join(' ');
}

/**
 * 找出有相同關聯標籤的產品
 */
export function findRelatedProducts(
  currentProduct: Product,
  allProducts: Product[],
  maxCount = 4
): Product[] {
  const currentTags = parseRelatedTags(currentProduct.related_tags);
  if (currentTags.length === 0) return [];

  return allProducts
    .filter(p => p.id !== currentProduct.id && p.status === 'active')
    .map(product => {
      const productTags = parseRelatedTags(product.related_tags);
      const matchCount = currentTags.filter(tag => productTags.includes(tag)).length;
      return { product, matchCount };
    })
    .filter(({ matchCount }) => matchCount > 0)
    .sort((a, b) => b.matchCount - a.matchCount)
    .slice(0, maxCount)
    .map(({ product }) => product);
}
